import { useState, useMemo, useCallback } from 'react';
import { Form } from '../../../../components';
import { handleError } from '../../../../lib/helpers';
import { useNotificationContext } from '../../../../providers';
import { useCloudAttributes } from '../hooks/useCloudAttributes';
import { useApplianceRedeployPrefill } from './useApplianceRedeployPrefill';
import { buildSourceCloudsPayload } from '../components/VMBasedMigrationsConfig/helpers';
import { useApplianceCommonState } from '../components/CommonApplianceConfiguration/useApplianceCommonState';

const buildAzureApplianceNetworkPayload = (values) => ({
    interfaces: {
        eth0: {
            ip_type: 'dhcp',
            type: 'Ethernet',
            dv_switch_name: '',
            network_name: values.subnet,
            assign_public_ip: values.assignPublicIp === 'dynamic',
            ip_addr: values.staticPrivateIp || '',
        },
    },
});

export const useAzureApplianceSetup = ({ cloudAccount, onSubmit, isRedeploy }) => {
    const [form] = Form.useForm();
    const { notifyError } = useNotificationContext();

    const [subscriptions, setSubscriptions] = useState([]);
    const [regions, setRegions] = useState([]);
    const [resourceGroups, setResourceGroups] = useState([]);
    const [virtualNetworks, setVirtualNetworks] = useState([]);
    const [subnets, setSubnets] = useState([]);
    const [instanceTags, setInstanceTags] = useState({});
    const [loadingRegionData, setLoadingRegionData] = useState(false);

    const subscription = Form.useWatch('subscription', form);
    const region = Form.useWatch('region', form);
    const virtualNetwork = Form.useWatch('virtualNetwork', form);

    const {
        useProxyEnabled,
        migrationInstructions,
        ignoreValidationErrors,
        commonConfigProps,
        prefillCommonFromAppliance,
    } = useApplianceCommonState(form);

    const handleAttributesLoaded = (result) => {
        if (result?.properties?.subscriptions) {
            setSubscriptions(result.properties.subscriptions);
        }
    };

    const { initialLoading, loadingError, loadCloudAttributes, fetchAttributes, loadingAttributes } =
        useCloudAttributes(cloudAccount, handleAttributesLoaded);

    useApplianceRedeployPrefill({
        isRedeploy,
        cloudAccount,
        subscriptions,
        form,
        fetchAttributes,
        prefillCommonFromAppliance,
        setRegions,
        setResourceGroups,
        setVirtualNetworks,
        setSubnets,
        setInstanceTags,
        setLoadingRegionData,
    });

    const handleSubscriptionChange = useCallback(
        (subscriptionId) => {
            setRegions([]);
            setResourceGroups([]);
            setVirtualNetworks([]);
            setSubnets([]);
            form.setFieldsValue({
                region: undefined,
                resourceGroup: undefined,
                virtualNetwork: undefined,
                subnet: undefined,
                assignPublicIp: 'none',
                staticPrivateIp: '',
            });
            const selectedSubscription = subscriptions.find((subscription) => subscription.id === subscriptionId);
            setRegions(selectedSubscription?.regions || []);
        },
        [form, subscriptions],
    );

    const handleRegionChange = useCallback(
        async (regionName) => {
            setResourceGroups([]);
            setVirtualNetworks([]);
            setSubnets([]);
            form.setFieldsValue({
                resourceGroup: undefined,
                virtualNetwork: undefined,
                subnet: undefined,
                assignPublicIp: 'none',
                staticPrivateIp: '',
            });

            setLoadingRegionData(true);
            try {
                const result = await fetchAttributes({
                    cloudAccountId: cloudAccount.id,
                    params: { region: regionName, subscription_id: subscription },
                }).unwrap();

                const subscriptionData = result?.properties?.subscriptions?.[0];
                if (subscriptionData) {
                    setResourceGroups(subscriptionData.resource_groups || []);
                    setVirtualNetworks(subscriptionData.regions?.[0]?.networks || []);
                }
            } catch (error) {
                handleError(error, notifyError, 'Failed to load region attributes');
            } finally {
                setLoadingRegionData(false);
            }
        },
        [form, cloudAccount, fetchAttributes, notifyError, subscription],
    );

    const handleVirtualNetworkChange = useCallback(
        (vnetValue) => {
            form.setFieldsValue({ subnet: undefined, assignPublicIp: 'none', staticPrivateIp: '' });
            const [vnetName, vnetResourceGroup] = (vnetValue || '').split('|');
            const vnet = virtualNetworks.find(
                (candidate) => candidate.name === vnetName && candidate.resource_group === vnetResourceGroup,
            );
            setSubnets(vnet?.subnets || []);
        },
        [form, virtualNetworks],
    );

    const handleFormSubmit = useCallback(
        (values) => {
            const [vnetName, vnetResourceGroup] = (values.virtualNetwork || '').split('|');

            const cloudProperties = {
                subscription_id: values.subscription,
                resource_group: values.resourceGroup,
                region: values.region,
                virtual_network: { name: vnetName, resource_group: vnetResourceGroup },
                tags: instanceTags || {},
                network: buildAzureApplianceNetworkPayload(values),
                vapp_name: values.vmName?.trim() || undefined,
                proxy_type: 'none',
            };

            const payload = {
                cloud_properties: cloudProperties,
                vm_level_migration_enabled: !!values.vmLevelEnabled,
                ignore_validation_errors: ignoreValidationErrors,
                ca_proxy: useProxyEnabled,
                migration_instructions: migrationInstructions,
            };

            if (values.vmLevelEnabled) {
                payload.source_clouds = buildSourceCloudsPayload(values);
            }

            onSubmit(payload);
        },
        [instanceTags, ignoreValidationErrors, useProxyEnabled, migrationInstructions, onSubmit],
    );

    const subscriptionOptions = useMemo(() => {
        return subscriptions.map((sub) => {
            return { value: sub.id, label: sub.name };
        });
    }, [subscriptions]);

    const regionOptions = useMemo(() => {
        return regions.map((item) => {
            return { value: item.name, label: item.label || item.name };
        });
    }, [regions]);

    const virtualNetworkOptions = useMemo(() => {
        return virtualNetworks.map((vnet) => ({
            value: `${vnet.name}|${vnet.resource_group}`,
            label: `${vnet.name} | ${vnet.resource_group}`,
        }));
    }, [virtualNetworks]);

    return {
        form,
        initialLoading,
        loadingError,
        loadCloudAttributes,
        loadingAttributes,
        loadingRegionData,
        subscription,
        region,
        virtualNetwork,
        subnets,
        resourceGroups,
        instanceTags,
        setInstanceTags,
        handleSubscriptionChange,
        handleRegionChange,
        handleVirtualNetworkChange,
        handleFormSubmit,
        subscriptionOptions,
        regionOptions,
        virtualNetworkOptions,
        commonConfigProps,
    };
};

